import isE2E from '~/modules/utils/is-e2-e';
import { RemoteMicOriginSetting } from '~/routes/settings/settings-state';

/**
 * Public origin used when the origin the game is served from can't be reached by another device.
 * Both ends only need to agree on the relay (VITE_APP_PARTYKIT_URL) to end up in the same room, so
 * the remote-mic page can be served from a completely different host than the game.
 */
export const PUBLIC_REMOTE_MIC_ORIGIN: string =
  import.meta.env.VITE_APP_REMOTE_MIC_ORIGIN || 'https://allkaraoke.party';

export const normaliseOrigin = (origin: string) => origin.trim().replace(/\/+$/, '');

export const isValidOrigin = (origin: string) => {
  try {
    const { protocol } = new URL(normaliseOrigin(origin));
    // A phone needs a secure context to reach its microphone, and http:// over a LAN IP isn't one.
    return protocol === 'https:';
  } catch {
    return false;
  }
};

const LOOPBACK_HOSTNAMES = ['localhost', '127.0.0.1', '[::1]', '::1'];

/** On another device a loopback address means that device, so a link to one can never be followed
 * from the phone the QR code is pointed at. Covers both the dev server and the desktop app, which
 * serves itself from localhost. */
const isReachableByAnotherDevice = (origin: string) => {
  try {
    return !LOOPBACK_HOSTNAMES.includes(new URL(origin).hostname);
  } catch {
    return false;
  }
};

/**
 * Origin that room links (the remote-mic QR code, the online lobby invite) should point at.
 *
 * The origin the game is served from wins whenever a phone could actually open it - that keeps PR
 * previews, LAN dev servers and self-hosted deployments working. A loopback origin can't be
 * opened, so it falls back to the public site, where the remote-mic page meets this game on the
 * relay by room code just the same.
 */
export const getRemoteMicOrigin = (): string => {
  const override = normaliseOrigin(RemoteMicOriginSetting.get());
  if (override) {
    return override;
  }

  const origin = normaliseOrigin(global.location?.origin ?? '');

  // The e2e suite runs the remote mic in a second browser on the same machine, where loopback is
  // the whole point - and it has no network to reach the public site with.
  if (isE2E() && origin) {
    return origin;
  }

  return isReachableByAnotherDevice(origin) ? origin : PUBLIC_REMOTE_MIC_ORIGIN;
};

/** `allkaraoke.party` - what to tell the player to type into their phone. */
export const getRemoteMicHostname = () => {
  try {
    return new URL(getRemoteMicOrigin()).host;
  } catch {
    return getRemoteMicOrigin();
  }
};
